import { styled } from '@mui/material/styles';
import { Box, Container, Button } from '@mui/material';

// Hero Section
export const HeroSection = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'heroImage',
})(({ theme, heroImage }) => ({
  minHeight: '85vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: heroImage
    ? `linear-gradient(90deg, rgba(32, 42, 36, 0.84) 0%, rgba(32, 42, 36, 0.54) 55%, rgba(32, 42, 36, 0.22) 100%), url("${heroImage}") center / cover`
    : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
  color: 'white',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(to bottom, rgba(0,0,0,0.05), rgba(0,0,0,0.18))',
  },
}));

export const HeroContent = styled(Container)(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  textAlign: 'left',
  padding: theme.spacing(4),
  marginLeft: 0,
  [theme.breakpoints.down('md')]: {
    textAlign: 'center',
    marginLeft: 'auto',
  },
}));

// Section Container
export const SectionContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 2),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(6, 2),
  },
}));

// Animated Box
export const AnimatedBox = styled(Box)(({ theme }) => ({
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
  },
}));

// Feature Card Container
export const FeatureCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.shape.borderRadius,
  background: 'white',
  boxShadow: theme.shadows[2],
  transition: 'all 0.3s ease',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  '&:hover': {
    boxShadow: theme.shadows[6],
    transform: 'translateY(-8px)',
  },
}));

// Gradient Button
export const GradientButton = styled(Button)(({ theme }) => ({
  background: theme.palette.secondary.main,
  color: 'white',
  padding: '12px 32px',
  fontSize: '1.1rem',
  borderRadius: 8,
  boxShadow: '0 4px 15px rgba(138, 90, 59, 0.25)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: theme.palette.secondary.dark,
    boxShadow: '0 6px 20px rgba(138, 90, 59, 0.35)',
    transform: 'translateY(-2px)',
  },
}));

// Page Title
export const PageTitle = styled('h1')(({ theme }) => ({
  fontSize: '3rem',
  fontWeight: 700,
  marginBottom: theme.spacing(2),
  color: theme.palette.primary.main,
  position: 'relative',
  display: 'inline-block',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-10px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '60px',
    height: '4px',
    background: theme.palette.secondary.main,
    borderRadius: '2px',
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '2.5rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '2rem',
  },
}));

// Loading Spinner Container
export const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '400px',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

// Footer Container
export const FooterContainer = styled(Box)(({ theme }) => ({
  background: theme.palette.primary.main,
  color: 'white',
  padding: theme.spacing(6, 2),
  marginTop: 'auto',
}));

// Image Overlay
export const ImageOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.7) 100%)',
  opacity: 0,
  transition: 'opacity 0.3s ease',
  display: 'flex',
  alignItems: 'flex-end',
  padding: theme.spacing(2),
  '&:hover': {
    opacity: 1,
  },
}));
